import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Ruler, Zap, Shield } from 'lucide-react';
import { useLanguage } from '../context/languagecontext';

export const CommercialShowcase = () => {
  const { t } = useLanguage();

  const capabilities = [
    { icon: Ruler, titleKey: 'commercial.anySize.title', descriptionKey: 'commercial.anySize.description' },
    { icon: Zap, titleKey: 'commercial.anyComplexity.title', descriptionKey: 'commercial.anyComplexity.description' },
    { icon: Building2, titleKey: 'commercial.multiUnit.title', descriptionKey: 'commercial.multiUnit.description' },
    { icon: Shield, titleKey: 'commercial.minimalDisruption.title', descriptionKey: 'commercial.minimalDisruption.description' }
  ];

  const projects = [
    { image: 'https://prolomov.net/picts/rezka/00019_.jpg', titleKey: 'commercial.projectHighRise', scaleKey: 'commercial.scaleLarge' },
    { image: 'https://mskdemontazh.ru/wp-content/uploads/2016/02/rezka-dvernogo-proema.jpg', titleKey: 'commercial.projectDemolition', scaleKey: 'commercial.scaleComplex' },
    { image: 'https://images.unsplash.com/photo-1645744240069-8081e7174de8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', titleKey: 'commercial.projectMultiUnit', scaleKey: 'commercial.scaleMultiStory' },
    { image: 'https://images.unsplash.com/photo-1747103068995-e6db935a922b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800', titleKey: 'commercial.projectRenovation', scaleKey: 'commercial.scalePrecision' }
  ];

  return (
    <section className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <div className="inline-block bg-orange-500 rounded-sm px-6 py-3 mb-6 shadow-hard">
            <span className="font-heading text-white text-lg font-bold uppercase tracking-wider">{t('commercial.capabilitiesTitle')}</span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4 uppercase tracking-wide">{t('commercial.mainTitle')}</h2>
          <p className="font-body text-xl text-gray-400 max-w-3xl mx-auto">{t('commercial.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {capabilities.map((capability, index) => {
            const IconComponent = capability.icon;
            return (
              <motion.div key={index} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ y: -8 }} className="bg-slate-800 border-2 border-slate-700 hover:border-orange-500 rounded-sm p-6 transition-all group">
                <div className="w-14 h-14 bg-orange-500 rounded-sm flex items-center justify-center mb-4 group-hover:shadow-hard-sm transition-all">
                  <IconComponent className="w-7 h-7 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3 uppercase tracking-wider">{t(capability.titleKey)}</h3>
                <p className="font-body text-gray-400 text-sm leading-relaxed">{t(capability.descriptionKey)}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }}>
          <h3 className="font-heading text-3xl font-bold text-white mb-8 text-center uppercase tracking-wider">{t('commercial.projectTypesTitle')}</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projects.map((project, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ y: -8 }} className="group relative rounded-sm overflow-hidden border-2 border-slate-700 hover:border-orange-500 transition-all shadow-lg hover:shadow-hard">
                <div className="aspect-square">
                  <img src={project.image} alt={t(project.titleKey)} className="w-full h-full object-cover grayscale-[40%] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="inline-block bg-orange-500 rounded-sm px-3 py-1 mb-2">
                      <span className="font-body text-white text-xs font-bold uppercase tracking-wider">{t(project.scaleKey)}</span>
                    </div>
                    <h4 className="font-heading text-white font-bold text-lg uppercase tracking-wider">{t(project.titleKey)}</h4>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
